import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ZenBackground } from "@/components/ZenBackground.tsx";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { PLATFORM_NAME } from "@/constants/settings.ts";
import { LabelInput } from "@/components/inputs/LabelInput.tsx";
import { ButtonGeneric } from "@/components/buttons/ButtonGeneric.tsx";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAdminGoogleSignin, postAdminRefresh } from "@/api/auth.ts";
import type { SimpleError } from "@/api/shared.ts";
import { toast } from "sonner";
import type { UnlockCallback } from "@/components/buttons/shared.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { toastAndError } from "@/helpers/toast.ts";
import { getMyDetails } from "@/api/user.ts";
import { IsAdmin } from "@/types/user.ts";
import { ButtonGoogleLogin } from "@/components/buttons/ButtonGoogleLogin.tsx";
import { JsonParse } from "@/helpers/json.ts";

export const Route = createFileRoute("/auth/login")({
  component: LoginComponent,
  head: () => ({
    meta: [{ title: "Log In | Eedi Tutor Dashboard" }],
  }),
});

const LoginWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  width: 100%;
  height: 100%;
`;

const LoginForm = styled.form`
  background: ${t.loginBackground};
  margin: auto;
  padding: 2rem;
  border-radius: 1rem;
  border: ${t.brandBorderSegment};
`;

const Header = styled.h1`
  font-size: var(--21px);
  margin-bottom: 1.5rem;
`;

const Label = styled.span`
  font-size: var(--18px);
  margin-bottom: 0.5rem;
`;

const OtherLoginWrapper = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 0.5rem;
`;

function LoginComponent() {
  const { auth, SetSession, SetUserDetails, Logout } = useUserStore();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const unlockCallback = useRef<UnlockCallback | null>(null);
  const done = useRef<boolean>(false);

  const loginQueryError = (error: Error) => {
    const err = JsonParse<SimpleError>(error.message);
    if (!err) {
      toast.error("Got a login error but was unable to parse json");
      return;
    }

    switch (err.statusCode) {
      case 400:
        toast.error(err.message);
        break;

      case 429:
        toast.error("Please wait before submitting again");
        break;

      default:
        toastAndError(err.message, "Login failed with unique error code", err.statusCode);
    }

    if (unlockCallback.current) {
      unlockCallback.current();
      unlockCallback.current = null;
    }
  };

  const loginQuery = useMutation({
    mutationFn: postAdminRefresh,
    onError: (error) => {
      loginQueryError(error);
    },
    onSuccess: (token) => {
      SetSession(token);
    },
  });

  const loginGoogleQuery = useMutation({
    mutationFn: getAdminGoogleSignin,
    onError: (error) => {
      loginQueryError(error);
    },
    onSuccess: (token) => {
      SetSession(token);
    },
  });

  const getUserDetails = useQuery({
    queryFn: getMyDetails,
    queryKey: ["me", auth.token],
    enabled: !!auth.token,
  });

  useEffect(() => {
    if (done.current) return;

    if (unlockCallback?.current && getUserDetails.isEnabled && getUserDetails.isFetched) {
      unlockCallback.current();
      unlockCallback.current = null;
    }

    if (getUserDetails.isSuccess) {
      if (!IsAdmin(getUserDetails.data.userRoles)) {
        toast.error("Account does not have access to this service.");
        Logout();
        return;
      }

      SetUserDetails(getUserDetails.data);
      done.current = true;

      navigate({
        to: "/",
      }).catch((e: Error) => {
        console.error("Could not redirect", e);
        window.location.assign("/");
      });
    } else if (getUserDetails.isError) {
      toastAndError("Failed to fetch tutor details, please try again later.", getUserDetails.error);
      Logout();
    }
  }, [unlockCallback, getUserDetails, Logout, navigate, SetUserDetails]);

  const submit = (callback: UnlockCallback | null) => {
    let bail = false;

    if (username.length === 0) {
      toast.error("Username cannot be empty");
      bail = true;
    }

    if (password.length === 0) {
      toast.error("Password cannot be empty");
      bail = true;
    }

    if (bail) {
      if (callback) callback();
      return;
    }

    unlockCallback.current = callback;

    loginQuery.mutate({
      username,
      password,
    });
  };

  return (
    <ZenBackground>
      <LoginWrapper>
        <LoginForm
          onSubmit={(e) => {
            e.preventDefault();
            // Disabled for now as most password managers will autoclick the 'onClick' button
            // submit(null);
          }}
        >
          <Header>Log in to {PLATFORM_NAME}</Header>
          <LabelInput
            label={<Label>Username</Label>}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setUsername(e.target.value);
            }}
            autoComplete="username"
            id="username"
          />
          <br />
          <LabelInput
            label={<Label>Password</Label>}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setPassword(e.target.value);
            }}
            type="password"
            autoComplete="current-password"
            id="password"
          />
          <br />
          {/* TODO: Switch button out for input type=submit */}
          <ButtonGeneric
            text={"Log in"}
            padding={"1.25rem"}
            backgroundColor={t.loginButtonColor}
            disabledBackgroundColor={t.brandSecondary}
            onClick={submit}
          />
          <OtherLoginWrapper>
            <p>Or</p>
            <ButtonGoogleLogin
              loginCallback={(data) => {
                loginGoogleQuery.mutate(data.credential);
              }}
            />
          </OtherLoginWrapper>
        </LoginForm>
      </LoginWrapper>
    </ZenBackground>
  );
}
